import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslate, useGetLocale, useInvalidate } from '@refinedev/core';
import { List } from '@refinedev/antd';
import {
  Table,
  Input,
  Form,
  Row,
  Col,
  Tag,
  Flex,
  Button,
  Typography,
  theme,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ContentDistributionPeriodOut } from '@/api/generated/schemas';
import { ModStatus, ProductType } from '@/api/generated/schemas';
import { productTypeColors } from '@/pages/content_catalog/products/constants';
import { ProductLazyCell } from '@/components/content_catalog/products/ProductLazyCell';
import { PublicationLazyCell } from '@/components/user_catalog/publications/PublicationLazyCell';
import { PublisherContextSwitcher } from '@/components/content_catalog/distribution_periods/PublisherContextSwitcher';
import { RefreshFromElibriButton } from '@/components/content_catalog/distribution_periods/RefreshFromElibriButton';
import { DistributionPeriodViewSelect } from '@/components/content_catalog/distribution_periods/DistributionPeriodViewSelect';
import { DistributionPeriodAlerts } from '@/components/content_catalog/distribution_periods/DistributionPeriodAlerts';
import { DateWindowPicker } from '@/components/content_catalog/distribution_periods/DateWindowPicker';
import { PublisherContractTag } from '@/components/content_catalog/distribution_periods/PublisherContractTag';
import { ShowLink } from '@/components/common/ShowLink';
import { ActiveFilters } from '@/components/common/ActiveFilters';
import {
  dimensionOf,
  isDateView,
  parseDateSelection,
  viewQueries,
  writeDateSelection,
} from '@/components/content_catalog/distribution_periods/summaryQueries';
import type {
  DateSelection,
  SummaryKey,
  SummaryQuery,
} from '@/components/content_catalog/distribution_periods/summaryQueries';
import { StatusDot } from '@/components/common/StatusDot';
import { PublicationBulkModStatusControl } from '@/components/user_catalog/publications/PublicationBulkModStatusControl';
import { ProductImportControl } from '@/components/user_catalog/publications/ProductImportControl';
import { PublicationSyncReviewControl } from '@/components/user_catalog/publications/PublicationSyncReviewControl';
import { CatalogSyncReviewControl } from '@/components/user_catalog/publications/CatalogSyncReviewControl';
import {
  importableProductIds,
  importableTitles,
  syncReviewIds,
  syncReviewTitles,
} from '@/pages/content_catalog/distribution_periods/importSelection';
import { statusTone } from '@/components/common/statusTone';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { useDragRowSelection } from '@/hooks/useDragRowSelection';
import type { CrudFilter } from '@refinedev/core';
import { SelectFilter } from '@/components/common/SelectFilter';
import { TextFilter } from '@/components/common/TextFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const DistributionPeriodList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const locale = useGetLocale()();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<ContentDistributionPeriodOut>({
      pagination: { pageSize: 20 },
      resource: 'content_catalog.distribution_periods',
      onSearch,
      filters: {
        initial:
          viewQueries().find((q) => q.key === 'available')?.filters ?? [],
      },
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  // The window is URL state so a reload keeps the date view it was read under.
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateSelection, setDateSelection] = useState<DateSelection>(() =>
    parseDateSelection(searchParams)
  );
  // The view is write-only state: picking one writes its filters to the URL,
  // and nothing reads a view back out of them. A link that already carries
  // filters therefore opens with no view named — the filters still apply.
  const [activeSummary, setActiveSummary] = useState<SummaryKey | null>(() =>
    [...searchParams.keys()].some((k) => k.startsWith('filters'))
      ? null
      : 'available'
  );

  const invalidate = useInvalidate();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // A bulk run outlives the selection it started from — see the bulk bar.
  const [bulkOpen, setBulkOpen] = useState(false);
  const rows = (tableProps.dataSource ?? []) as ContentDistributionPeriodOut[];
  // Selection is of visible rows: an id left behind by a page or view change
  // would inflate the count while the bulk action acted on fewer publications.
  const visibleSelectedIds = selectedIds.filter((id) =>
    rows.some((r) => r.id === id)
  );
  // Press a checkbox and drag to select (or deselect) a run of rows.
  const drag = useDragRowSelection<number>({
    keys: rows.map((r) => r.id),
    selected: visibleSelectedIds,
    onChange: setSelectedIds,
  });
  // Bulk target is the user-catalog publication, not the period itself.
  const selectedPublicationIds = syncReviewIds(rows, visibleSelectedIds);
  const selectedPublicationTitles = syncReviewTitles(rows, visibleSelectedIds);
  // Selected formats: the cascade and licence check act on these only.
  const selectedProductIds = Array.from(
    new Set(
      rows
        .filter((r) => visibleSelectedIds.includes(r.id))
        .map((r) => r.user_catalog_product_id)
        .filter((id): id is number => id != null)
    )
  );
  const importProductIds = importableProductIds(rows, visibleSelectedIds);
  const importTitles = importableTitles(rows, visibleSelectedIds);
  const onBulkDone = () =>
    invalidate({
      resource: 'content_catalog.distribution_periods',
      invalidates: ['list'],
    });

  const { token } = theme.useToken();
  const publisherFilter = (filters as CrudFilter[]).find(
    (f) => 'field' in f && f.field === 'publisher_id' && f.value != null
  );
  const publisherId =
    publisherFilter && 'value' in publisherFilter
      ? Number(publisherFilter.value)
      : null;
  const scope: CrudFilter[] =
    publisherId != null
      ? [{ field: 'publisher_id', operator: 'eq', value: publisherId }]
      : [];
  const setPublisherScope = (id: number | null) => {
    setSelectedIds([]);
    setFilters(
      [{ field: 'publisher_id', operator: 'eq', value: id ?? undefined }],
      'merge'
    );
  };
  const total =
    typeof tableProps.pagination === 'object'
      ? tableProps.pagination.total
      : undefined;

  const applySummary = (query: SummaryQuery | null) => {
    setActiveSummary(query?.key ?? null);
    setSelectedIds([]);
    searchFormProps.form?.setFieldsValue({ q: '' });
    setFilters([...scope, ...(query?.filters ?? [])], 'replace');
  };
  /** Select by key; date views resolve through the current window. */
  const applyView = (key: SummaryKey | null, selection = dateSelection) =>
    applySummary(
      key
        ? (viewQueries(undefined, selection).find((q) => q.key === key) ?? null)
        : null
    );
  const applyDateSelection = (selection: DateSelection) => {
    setDateSelection(selection);
    setSearchParams((prev) => writeDateSelection(prev, selection), {
      replace: true,
    });
    if (isDateView(activeSummary)) applyView(activeSummary, selection);
  };
  /** Enum chips read as their labels; dates and ids fall back to the default. */
  const periodFilterValue = (
    field: string,
    value: string | number | boolean
  ) => {
    if (field === 'is_distributable')
      return t(value ? 'common.yes' : 'common.no');
    if (field === 'publisher_has_active_contract')
      return t(
        `content_catalog.publishers.contract.${value ? 'active' : 'none'}`
      );
    if (field === 'status')
      return t(`content_catalog.distribution_periods.filters.${value}`);
    if (field === 'product_type')
      return t(`content_catalog.products.product_type.${value}`);
    if (field.endsWith('mod_status')) return t(`base.mod_status.${value}`);
    return undefined;
  };
  /**
   * Removing one of a view's own filters means the rows are no longer that
   * view, so it stops being named — column filters leave the name alone.
   */
  const dropViewLabelIfPartOfIt = (field: string) => {
    const view = viewQueries(undefined, dateSelection).find(
      (q) => q.key === activeSummary
    );
    const owns = (view?.filters ?? []).some(
      (f) => 'field' in f && f.field === field
    );
    if (owns) setActiveSummary(null);
  };

  return (
    <List
      canCreate={false}
      title={
        <Flex vertical gap={token.marginXXS}>
          <Flex align="center" gap={token.marginSM} wrap>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {t('content_catalog.distribution_periods.titles.list')}
            </Typography.Title>
            <Typography.Text
              type="secondary"
              style={{ fontSize: token.fontSizeHeading3 }}
            >
              /
            </Typography.Text>
            <PublisherContextSwitcher
              value={publisherId}
              onChange={setPublisherScope}
            />
          </Flex>
          {total != null && (
            <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
              {t('content_catalog.distribution_periods.context.total', {
                total,
              })}
            </Typography.Text>
          )}
        </Flex>
      }
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <CatalogSyncReviewControl onDone={onBulkDone} />
          <RefreshFromElibriButton />
        </>
      )}
    >
      <DistributionPeriodAlerts
        activeKey={activeSummary}
        scope={scope}
        onSelect={applySummary}
      />
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <DistributionPeriodViewSelect
              value={activeSummary}
              onChange={(key) => applyView(key)}
            />
          </Col>
          {isDateView(activeSummary) && (
            <Col>
              <DateWindowPicker
                dimension={dimensionOf(activeSummary)}
                scope={scope}
                value={dateSelection}
                onChange={applyDateSelection}
              />
            </Col>
          )}
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t(
                  'content_catalog.distribution_periods.search.placeholder'
                )}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {activeSummary && (
        <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
          {t(
            `content_catalog.distribution_periods.view_descriptions.${activeSummary}`
          )}
        </Typography.Paragraph>
      )}

      <ActiveFilters
        filters={filters as CrudFilter[]}
        setFilters={setFilters}
        labelPrefix="content_catalog.distribution_periods"
        // The switcher and the search box speak for these two.
        ignore={['publisher_id', 'q']}
        baseFields={['created_at', 'updated_at']}
        formatValue={periodFilterValue}
        onRemove={dropViewLabelIfPartOfIt}
      />

      {/* The bar also stays while the modal is open: the import and status
          runs refetch the list after every step, and the rows they changed
          can leave the view (the default one lists enabled formats only).
          Unmounting here would take the progress modal with it. The sync
          review refetches once, on close, for the same reason in reverse:
          each refetch fans out to every view chip's count query. */}
      {(visibleSelectedIds.length > 0 || bulkOpen) && (
        <Flex
          align="center"
          gap={token.marginSM}
          style={{ marginBottom: token.marginSM }}
        >
          {visibleSelectedIds.length > 0 && (
            <Typography.Text>
              {t('user_catalog.common.bulk_mod_status.selected', {
                count: visibleSelectedIds.length,
              })}
              {' · '}
              {t('content_catalog.distribution_periods.bulk.publications', {
                count: selectedPublicationIds.length,
              })}
              {importProductIds.length > 0 && (
                <>
                  {' · '}
                  {t('content_catalog.distribution_periods.bulk.importable', {
                    count: importProductIds.length,
                  })}
                </>
              )}
            </Typography.Text>
          )}
          <ProductImportControl
            ids={importProductIds}
            titles={importTitles}
            onDone={onBulkDone}
            onClosed={() => setSelectedIds([])}
            onOpenChange={setBulkOpen}
          />
          <PublicationSyncReviewControl
            ids={selectedPublicationIds}
            titles={selectedPublicationTitles}
            onDone={onBulkDone}
            onClosed={() => setSelectedIds([])}
            onOpenChange={setBulkOpen}
          />
          <PublicationBulkModStatusControl
            ids={selectedPublicationIds}
            productIds={selectedProductIds}
            onDone={onBulkDone}
            onClosed={() => setSelectedIds([])}
            onOpenChange={setBulkOpen}
          />
          <Button type="link" size="small" onClick={() => setSelectedIds([])}>
            {t('actions.clear')}
          </Button>
        </Flex>
      )}

      <Table
        {...rowNav('content_catalog.distribution_periods', {
          ...tableProps,
          onRow: drag.onRow,
        })}
        rowSelection={{
          selectedRowKeys: drag.selectedRowKeys,
          onChange: (keys) => setSelectedIds(keys.map(Number)),
        }}
        rowKey="id"
        pagination={{
          ...(typeof tableProps.pagination === 'object'
            ? tableProps.pagination
            : {}),
          showTotal: (total) => t('base.total', { total }),
        }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column<ContentDistributionPeriodOut>
          dataIndex="product_id"
          title={t('content_catalog.distribution_periods.fields.product')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_id')}
          render={(value: number, record) => (
            <ProductLazyCell
              productId={value}
              fallbackTitle={record.product_title}
            />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="product_id"
              width={160}
              placeholder={t(
                'content_catalog.distribution_periods.fields.product_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="product_type"
          title={t('content_catalog.distribution_periods.fields.product_type')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_type')}
          render={(value: string) =>
            value ? (
              <Tag color={productTypeColors[value] || 'default'}>
                {t(`content_catalog.products.product_type.${value}`)}
              </Tag>
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_type')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="product_type"
              minWidth={160}
              placeholder={t(
                'content_catalog.distribution_periods.fields.product_type'
              )}
              options={Object.values(ProductType).map((v) => ({
                label: t(`content_catalog.products.product_type.${v}`),
                value: v,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column<ContentDistributionPeriodOut>
          dataIndex="publisher_id"
          title={t('content_catalog.distribution_periods.fields.publisher')}
          render={(_, record) =>
            record.publisher_name ? (
              <Flex vertical>
                <Flex gap={token.marginXS} align="center" wrap>
                  {record.publisher_id != null ? (
                    <ShowLink
                      resource="content_catalog.publishers"
                      id={record.publisher_id}
                    >
                      {record.publisher_name}
                    </ShowLink>
                  ) : (
                    <span>{record.publisher_name}</span>
                  )}
                  <PublisherContractTag
                    hasActiveContract={record.publisher_has_active_contract}
                  />
                </Flex>
                {record.publisher_legal_name && (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: token.fontSizeSM }}
                  >
                    {record.publisher_legal_name}
                  </Typography.Text>
                )}
              </Flex>
            ) : (
              '-'
            )
          }
          // The publisher scope itself is owned by the context switcher in
          // the page title; this filter only narrows by contract status.
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'publisher_has_active_contract'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter<boolean>
              field="publisher_has_active_contract"
              options={[
                {
                  label: t('content_catalog.publishers.contract.active'),
                  value: true,
                },
                {
                  label: t('content_catalog.publishers.contract.none'),
                  value: false,
                },
              ]}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column<ContentDistributionPeriodOut>
          dataIndex="started_at"
          title={t('content_catalog.distribution_periods.fields.period')}
          sorter
          sortOrder={getSortOrder(sorters, 'started_at')}
          render={(value: string, record) => (
            <Flex gap={token.marginXS} align="center" wrap>
              <span style={{ whiteSpace: 'nowrap' }}>
                {value ? new Date(value).toLocaleDateString(locale) : '-'}
                {' → '}
                {record.ended_at
                  ? new Date(record.ended_at).toLocaleDateString(locale)
                  : '∞'}
              </span>
              {/* New titles sell best, so upcoming periods are flagged. */}
              {value && new Date(value) > new Date() && (
                <Tag color="gold">
                  {t('content_catalog.distribution_periods.upcoming_tag')}
                </Tag>
              )}
            </Flex>
          )}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleDateString(locale) : '-'
          }
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string | null) =>
            value ? new Date(value).toLocaleDateString(locale) : '-'
          }
        />

        <Table.Column
          dataIndex="user_catalog_publication_id"
          title={t(
            'content_catalog.distribution_periods.fields.user_catalog_publication_id'
          )}
          sorter
          sortOrder={getSortOrder(sorters, 'user_catalog_publication_id')}
          render={(
            value: number | null | undefined,
            record: ContentDistributionPeriodOut
          ) =>
            value != null ? (
              <PublicationLazyCell
                publicationId={value}
                title={record.user_catalog_publication_title}
                coverUrl={record.user_catalog_publication_cover_url}
              />
            ) : (
              '-'
            )
          }
        />

        {(
          [
            'user_catalog_publication_mod_status',
            'user_catalog_product_mod_status',
          ] as const
        ).map((field) => (
          <Table.Column
            key={field}
            dataIndex={field}
            title={t(`content_catalog.distribution_periods.fields.${field}`)}
            sorter
            sortOrder={getSortOrder(sorters, field)}
            align="center"
            render={(value: ModStatus | null | undefined) =>
              value ? (
                <StatusDot
                  tone={statusTone(value)}
                  title={`${t(`content_catalog.distribution_periods.fields.${field}`)}: ${t(`base.mod_status.${value}`)}`}
                />
              ) : (
                '-'
              )
            }
            filtered={isFieldFiltered(filters as CrudFilter[], field)}
            filterDropdown={({ confirm, clearFilters }) => (
              <SelectFilter
                field={field}
                minWidth={160}
                placeholder={t(
                  `content_catalog.distribution_periods.fields.${field}`
                )}
                options={Object.values(ModStatus).map((value) => ({
                  label: t(`base.mod_status.${value}`),
                  value,
                }))}
                filters={filters as CrudFilter[]}
                confirm={confirm}
                clearFilters={clearFilters}
                setFilters={setFilters}
              />
            )}
          />
        ))}
      </Table>
    </List>
  );
};
